import { YieldStrings } from '../../constants';

/**
 * Helper function to update task status with tracking ID
 * @param {string} trackingId - The tracking ID from the response
 * @param {Function} updateTasksStatus - Function to update task status
 * @returns {Promise<Object|undefined>}
 */
const updateTaskStatus = async (trackingId, updateTasksStatus) => {
  if (!trackingId) return undefined;

  const selectedCropSeasons = JSON.parse(
    localStorage.getItem('selectedCropSeasons')
  );
  const updateResult = await updateTasksStatus(trackingId, selectedCropSeasons);
  return updateResult;
};

/**
 * Shows a success toast notification
 * @param {Function} setToast - Function to set toast state
 * @param {Function} setShowToast - Function to show toast
 */
const showSuccessToast = (setToast, setShowToast) => {
  setToast({
    type: 'success',
    title: 'All Loads saved.',
    message: '',
    showToast: true
  });
  setShowToast(true);
};

/**
 * Handles 504 timeout error scenario
 * @param {Object} params - Handler parameters
 * @returns {Promise<boolean>} Returns false to indicate task status should not be refreshed
 */
const handle504TimeoutError = async ({
  errorTrackingId,
  updateTasksStatus,
  taskId,
  removeBannerFromUI,
  setToast,
  setShowToast
}) => {
  // Acknowledge the task so it doesn't reappear later
  if (errorTrackingId) {
    await updateTaskStatus(errorTrackingId, updateTasksStatus);
  }

  // Remove banner from UI immediately to prevent it from reappearing
  if (removeBannerFromUI) {
    removeBannerFromUI(taskId);
  }

  setToast({
    type: 'warning',
    title: YieldStrings.toast.timeoutWarningTitle,
    message: YieldStrings.toast.timeoutWarningContent,
    showToast: true
  });
  setShowToast(true);

  return false; // Don't refresh task status
};

/**
 * Handles non-504 error scenario
 * @param {Object} params - Handler parameters
 */
const handleGeneralError = async ({
  errorTrackingId,
  updateTasksStatus,
  setToast,
  setShowToast
}) => {
  // For non-504 errors, update task status
  if (errorTrackingId) {
    await updateTaskStatus(errorTrackingId, updateTasksStatus);
  }

  setToast({
    type: 'error',
    title: 'Unable to retry load.',
    message: 'An error prevented your load from being retried.',
    showToast: true
  });
  setShowToast(true);
};

/**
 * Handles retrying a failed task
 * @param {string} taskId - The ID of the task to retry
 * @param {Object} dependencies - Dependencies required for the retry operation
 * @param {Function} dependencies.retryTasksStatus - Function to call the retry API
 * @param {Function} dependencies.updateTasksStatus - Function to update task status with tracking ID
 * @param {Function} dependencies.removeBannerFromUI - Function to remove banner from local state (prevents reappearing on 504)
 * @param {Function} dependencies.setToast - Function to set toast notification state
 * @param {Function} dependencies.setShowToast - Function to show/hide toast
 * @param {Function} dependencies.getYieldLoads - Function to refresh loads data
 * @param {Function} dependencies.refreshTasksStatus - Function to refresh task status
 * @returns {Promise<void>}
 */
export const retryTask = async (
  taskId,
  {
    retryTasksStatus,
    updateTasksStatus,
    removeBannerFromUI,
    setToast,
    setShowToast,
    getYieldLoads,
    refreshTasksStatus
  }
) => {
  let shouldRefreshTaskStatus = true;

  try {
    const { trackingId } = await retryTasksStatus(taskId);

    // Update task status for successful retry
    const updateResult = await updateTaskStatus(trackingId, updateTasksStatus);
    if (trackingId && !updateResult?.success) {
      throw new Error('Task status update failed');
    }

    showSuccessToast(setToast, setShowToast);
  } catch (error) {
    const errorTrackingId = error.response?.headers?.['x-request-tracking-id'];

    if (error.response?.status === 504) {
      shouldRefreshTaskStatus = await handle504TimeoutError({
        errorTrackingId,
        updateTasksStatus,
        taskId,
        removeBannerFromUI,
        setToast,
        setShowToast
      });
    } else {
      await handleGeneralError({
        errorTrackingId,
        updateTasksStatus,
        setToast,
        setShowToast
      });
    }
  } finally {
    if (getYieldLoads) {
      getYieldLoads();
    }
    if (refreshTasksStatus && shouldRefreshTaskStatus) {
      refreshTasksStatus();
    }
  }
};

/**
 * Handles dismissing a completed task banner
 * @param {string} taskId - The ID of the task to dismiss
 * @param {string} seasonId - The season ID associated with the task
 * @param {Object} dependencies - Dependencies required for the dismiss operation
 * @param {Function} dependencies.updateTasksStatus - Function to update task status
 * @param {Function} dependencies.removeBannerFromUI - Function to remove banner from UI
 * @returns {Promise<void>}
 */
export const dismissTask = async (
  taskId,
  seasonId,
  { updateTasksStatus, removeBannerFromUI }
) => {
  // Convert single seasonId to array as updateTasksStatus expects seasonIds array
  const result = await updateTasksStatus(taskId, [seasonId]);

  if (!result.success) {
    // Failed to dismiss task - don't remove from UI
    return;
  }

  // Remove the dismissed task from the taskStatusData immediately
  removeBannerFromUI(taskId);
};
